import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Grid2, List, ListItem, ListItemIcon, ListItemText, MenuItem, Select, Typography, useTheme, Card, Skeleton } from "@mui/material";
import { Circle } from "@mui/icons-material";
import { FaSquareFull } from "react-icons/fa6";
import { useGetPlanVsActualQuery } from "store/slices/apis/scheduleDashboarSlice";
import { useParams } from "react-router-dom";


const PlanActualGraph = () => {
    const [chartData, setChartData] = useState([]);

    // Grid item layout: 4 columns on desktop (md=3)
    const gridItemProps = { xs: 12, sm: 6, md: 3 };
    const theme = useTheme();
    const { projectId } = useParams();
    const { data, isLoading } = useGetPlanVsActualQuery({ projectId });

    useEffect(() => {
        if (data) {

            setChartData([data]);


            const { overallTarget, plannedPercentage, actualPercentage } = data;

            const updatedChartData = [
                {
                    name: "Overall Progress",
                    data: [
                        {
                            y: overallTarget,
                            color: "#E9E3E3",
                            radius: "90%",
                            innerRadius: "100%",
                        },
                    ],
                },
                {
                    name: "Planned Progress",
                    data: [
                        {
                            y: plannedPercentage,
                            color: "#295477",
                            radius: "75%",
                            innerRadius: "85%",
                        },
                    ],
                },
                {
                    name: "Actual Progress",
                    data: [
                        {
                            y: actualPercentage,
                            color: actualPercentage >= plannedPercentage ? '#3E756D' : '#E72B33',
                            radius: "60%",
                            innerRadius: "70%",
                        },
                    ],
                },
            ];

            setChartData(updatedChartData);
        }
    }, [data]);


    let options = {
        chart: {
            type: "solidgauge",
            height: 140,
            width: 140,
            backgroundColor: 'transparent',
        },
        title: {
            text: "",
            style: { fontSize: "18px" },
        },
        pane: {
            size: '100%',
            startAngle: 0,
            endAngle: 360,
            background: [
                {
                    outerRadius: "",
                    innerRadius: "88%",
                    backgroundColor: Highcharts.color("#7cb5ec").setOpacity(0.3).get(),
                    borderWidth: 0,
                },
                {
                    outerRadius: "",
                    innerRadius: "63%",
                    backgroundColor: Highcharts.color("#90ed7d").setOpacity(0.3).get(),
                    borderWidth: 0,
                },
                {
                    outerRadius: "",
                    innerRadius: "38%",
                    backgroundColor: Highcharts.color("#f7a35c").setOpacity(0.3).get(),
                    borderWidth: 0,
                },
            ],
        },
        yAxis: {
            min: 0,
            max: 100,
            lineWidth: 0,
            tickPositions: [],
        },
        plotOptions: {
            solidgauge: {
                dataLabels: {
                    enabled: false,
                },
                linecap: "round",
                stickyTracking: false,
            },
        },
        tooltip: {
            enabled: false
        },
        legend: {
            enabled: false,
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
        },
        series: chartData,
        credits: {
            enabled: false,
        },
    };

    return (
        <Box sx={{ padding: 2, pb: 0, border: '1px solid #2525252E', borderRadius: '7px', minHeight: '230px', background: "#FFFFFF", height: "100%" }}>
            <Grid2 container>
                <Grid2 size={{ xs: 12, md: 12 }}>
                    <Typography variant="h6" sx={{ fontWeight: "500", fontSize: '18px', color: `${theme?.customSettings?.themeColor}`, lineHeight: '100%', letterSpacing: '-0.4px' }}>
                        Physical Progress - Plan vs Actual
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: "500", fontSize: '14px', color: `#182A4E80`, lineHeight: '100%', letterSpacing: '-0.4px' }}>
                        Progress variance and  execution shortfalls against the project baseline
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    {isLoading ? (
                        <List sx={{ padding: 0, mt: 2 }}>
                            {[...Array(2)].map((_, index) => (
                                <ListItem sx={{ paddingLeft: 1 }} key={index}>
                                    <ListItemIcon sx={{ minWidth: "25px" }}>
                                        <Skeleton variant="circular" width={14} height={14} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={<Skeleton variant="text" width={100} height={20} />}
                                        sx={{
                                            "& .MuiTypography-root": {
                                                fontSize: "16px",
                                            },
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : chartData.length === 0 ||
                        chartData.every(series => !series?.data?.[0]?.y && series?.data?.[0]?.y !== 0) ? (
                        <Typography
                            sx={{
                                mt: 5,
                                textAlign: "center",
                                width: "100%",
                                fontSize: "16px",
                                color: "#999",
                            }}
                        >
                            No record found
                        </Typography>
                    ) : (
                        <List sx={{ padding: 0, mt: 2 }}>
                            {chartData?.map((val, index) => {
                                if (index !== 0) {
                                    return (
                                        <ListItem sx={{ paddingLeft: 1 }} key={val.name}>
                                            <ListItemIcon sx={{ minWidth: "25px" }}>
                                                <FaSquareFull
                                                    style={{
                                                        color: `${val?.data[0]?.color}`,
                                                        height: "14px",
                                                        width: "14px",
                                                    }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <>
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                fontWeight: 500,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "-0.4px",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                        >
                                                            {val?.name}:{" "}
                                                        </Typography>
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                color: "#182A4E",
                                                                fontWeight: 500,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "-0.4px",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                        >
                                                            {val?.data[0]?.y}%
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                        </ListItem>
                                    );
                                }
                                return null;
                            })}
                        </List>
                    )}
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }} >
                    {isLoading ? (
                        <Skeleton variant="circular" width={130} height={130} animation="wave" sx={{ marginTop: '10px' }} />
                    ) : (
                        <Box sx={{ marginLeft: '40px' }}>
                            <HighchartsReact highcharts={Highcharts} options={options} />
                        </Box>
                    )}
                </Grid2>
            </Grid2>
        </Box>
    );
};

export default PlanActualGraph;